import React, { useState } from "react";
import { useCategories } from "@/hooks/useCategories";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { Category } from "@/lib/types";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { CategoryModal } from "@/components/categories/CategoryModal";
import { useTranslation } from "react-i18next";

export function CategoriesStep() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { data: categories = [], isLoading } = useCategories().useCategoriesQuery();
  const createCategory = useCategories().useCreateCategoryMutation();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const DEFAULT_CATEGORIES = [
    { 
      name: t('categories.software.name', "Software"), 
      description: t('categories.software.description', "Software licenses and subscriptions") 
    },
    { 
      name: t('categories.hardware.name', "Hardware"), 
      description: t('categories.hardware.description', "Physical hardware and equipment") 
    },
    { 
      name: t('categories.services.name', "Services"), 
      description: t('categories.services.description', "Professional and consulting services") 
    },
    { 
      name: t('categories.utilities.name', "Utilities"), 
      description: t('categories.utilities.description', "Utilities like electricity, water, etc.") 
    },
    { 
      name: t('categories.rent.name', "Rent"), 
      description: t('categories.rent.description', "Office and workspace rentals") 
    },
  ];

  const handleAddDefaultCategories = async () => {
    try {
      for (const category of DEFAULT_CATEGORIES) {
        await createCategory.mutateAsync({
          name: category.name,
          description: category.description,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
      }
      toast.success(t('defaultCategoriesAdded', "Default categories added"));
    } catch (error) {
      console.error("Error adding default categories:", error);
      toast.error(t('errorAddingDefaultCategories', "Failed to add default categories"));
    }
  };

  if (isLoading) {
    return <div>Loading categories...</div>;
  }

  return (
    <div className="space-y-6">
      <p>
        {t('categories.intro', "Categories help you organize your contracts and filter them more effectively. Add some categories that match your organization's needs.")}
      </p>

      {categories.length === 0 ? (
        <div className="text-center p-6 border rounded-md bg-muted/50">
          <h3 className="text-lg font-medium mb-4">{t('categories.noCategories', "No Categories Yet")}</h3>
          <p className="text-muted-foreground mb-6">
            {t('categories.empty', "You haven't created any categories yet. Add some to help organize your contracts.")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={handleAddDefaultCategories}>
              {t('categories.addDefaults', "Add Default Categories")}
            </Button>
            <Button variant="outline" onClick={() => setIsModalOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t('categories.createCustom', "Create Custom Category")}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {categories.map((category) => (
              <div
                key={category.id}
                className="border rounded-md p-4 bg-card"
              >
                <h3 className="font-medium">{category.name}</h3>
                {category.description && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {category.description}
                  </p>
                )}
              </div>
            ))}
          </div>

          <Button onClick={() => setIsModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('categories.addCategory', "Add Category")}
          </Button>
        </div>
      )}

      <CategoryModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["categories"] });
        }}
      />
    </div>
  );
}
